import React, { useMemo } from 'react';
import styled from 'styled-components';
import type { CellTextAlign } from '~/shared/components/Table/cellAlignment';
import {
  parseHeadlessTableData,
  resolveHeadlessColumnCount,
  resolveHeadlessColumnWidth,
  type HeadlessTableColNode,
} from './parseHeadlessTableData';

const Wrapper = styled.div<{ $gridheight?: number }>`
  height: ${({ $gridheight }) => (!$gridheight ? 'auto' : `${Math.floor($gridheight) * 102}px`)};
  background-color: var(--table-wrapper-background-color, #fff);
  border-radius: 4px;
  padding: 15px 10px 20px;
  width: 100%;
  max-width: 100%;
  margin-top: 10px;
  overflow: auto;
`;

const Title = styled.div`
  color: #4c5e72;
  font-size: 18px;
  font-weight: 500;
  font-family: Montserrat, sans-serif;
  line-height: 20px;
  padding: 0 0 12px 0;
`;

const Subtitle = styled.div`
  color: #4c5e72;
  font-size: 14px;
  font-weight: 500;
  font-family: Montserrat, sans-serif;
  margin-top: -6px;
  margin-bottom: 10px;
`;

const TableRoot = styled.div`
  width: 100%;
  border-top: 1px solid #e0e0e0;
`;

const Row = styled.div<{ $isSection?: boolean }>`
  display: flex;
  width: 100%;
  color: #4c5e72;
  font-size: 15px;
  font-weight: ${({ $isSection }) => ($isSection ? 600 : 400)};
  border-bottom: 1px solid #e0e0e0;
  border-left: 1px solid #e0e0e0;
  border-right: 1px solid #e0e0e0;
  min-height: ${({ $isSection }) => ($isSection ? '36px' : '32px')};
  background: ${({ $isSection }) => ($isSection ? 'rgba(212, 229, 255, 0.35)' : 'transparent')};
`;

const Cell = styled.div<{ $align: CellTextAlign; $width: string; $isSection?: boolean }>`
  flex: 0 0 ${({ $width }) => $width};
  width: ${({ $width }) => $width};
  max-width: ${({ $width }) => $width};
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: ${({ $align }) => ($align === 'left' ? 'flex-start' : $align === 'right' ? 'flex-end' : 'center')};
  text-align: ${({ $align }) => $align};
  padding: 6px 12px;
  font-weight: ${({ $isSection }) => ($isSection ? 600 : 'inherit')};
  overflow-wrap: anywhere;
`;

type HeadlessTableChartProps = {
  data: {
    title?: string;
    subtitle?: string;
    col_nodes?: HeadlessTableColNode[];
    data?: unknown;
  };
  chartTitle?: string;
  chartSubtitle?: string;
  gridHeight?: number;
};

export default function HeadlessTableChart({ data, chartTitle, chartSubtitle, gridHeight }: HeadlessTableChartProps) {
  const title = (chartTitle || data?.title || '').replace('\u05bf', '').trim();
  const subtitle = (chartSubtitle || data?.subtitle || '').replace('\u05bf', '').trim();
  const colNodes = data?.col_nodes;

  const rows = useMemo(() => parseHeadlessTableData(data?.data), [data?.data]);
  const columnCount = useMemo(() => resolveHeadlessColumnCount(colNodes, rows), [colNodes, rows]);

  const columnMeta = useMemo(() => {
    return Array.from({ length: columnCount }, (_, index) => {
      const col = colNodes?.[index];
      // Headless table defaults: text/label column left, value column(s) right.
      return {
        width: resolveHeadlessColumnWidth(col, columnCount, index),
        align: (index === 0 ? 'left' : 'right') as CellTextAlign,
      };
    });
  }, [colNodes, columnCount]);

  return (
    <Wrapper $gridheight={gridHeight} data-testid="headless-table-chart">
      {title ? <Title>{title}</Title> : null}
      {subtitle ? <Subtitle>{subtitle}</Subtitle> : null}
      <TableRoot role="table" aria-label={title || 'Headless table'}>
        {rows.map((row, rowIndex) => {
          if (row.kind === 'section') {
            return (
              <Row key={`section-${rowIndex}-${row.label}`} $isSection role="row">
                {columnMeta.map((col, colIndex) => (
                  <Cell
                    key={`section-${rowIndex}-${colIndex}`}
                    role="cell"
                    $align={colIndex === 0 ? 'left' : col.align}
                    $width={col.width}
                    $isSection
                  >
                    {colIndex === 0 ? row.label : ''}
                  </Cell>
                ))}
              </Row>
            );
          }

          return (
            <Row key={`metric-${rowIndex}`} role="row">
              {columnMeta.map((col, colIndex) => (
                <Cell key={`metric-${rowIndex}-${colIndex}`} role="cell" $align={col.align} $width={col.width}>
                  {row.cells[colIndex] ?? ''}
                </Cell>
              ))}
            </Row>
          );
        })}
      </TableRoot>
    </Wrapper>
  );
}
